import * as React from 'react';
import { createPortal } from 'react-dom';
import { ClubProgrammeDataTableShell } from './ClubProgrammeDataTableShell';

const IconWhistle: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    width="17"
    height="17"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.85"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z" />
  </svg>
);

const IconMore: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 6a1 1 0 1 0 0-2 1 1 0 0 0 0 2M12 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2M12 20a1 1 0 1 0 0-2 1 1 0 0 0 0 2" />
  </svg>
);

type RowActionIconName = 'form' | 'mail' | 'lock' | 'trash';

const ROW_ACTION_ICON_PATHS: Record<RowActionIconName, string> = {
  form: 'M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01',
  mail: 'M4 4h16v16H4zM4 7l8 6 8-6',
  lock: 'M6 11V8a6 6 0 0 1 12 0v3M5 11h14a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1z',
  trash: 'M3 6h18M8 6V4h8v2M6 6l1 15h10l1-15M10 10v7M14 10v7'
};

const RowActionIcon: React.FC<{ icon: RowActionIconName }> = ({ icon }) => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d={ROW_ACTION_ICON_PATHS[icon]} />
  </svg>
);

export type ManageClubColumnKey = 'club' | 'dateTime' | 'audience' | 'places' | 'bookingForm' | 'invites' | 'status';

export type ManageClubColumn = {
  field: ManageClubColumnKey;
  text: string;
  width: number;
};

type Props = {
  rows: any[];
  columns: ManageClubColumn[];
  selectedProgrammeClubs: any[];
  canEdit: boolean;
  isBulkDisabling: boolean;
  savingIds: { [k: string]: boolean };
  onToggleRowSelection: (row: any) => void;
  onToggleEnabled: (programmeClubId: string, isEnabled: boolean) => void;
  onRowClick: (row: any) => void;
  onOpenBookingForm: (row: any) => void;
  onOpenInvites: (row: any) => void;
  onRequestRemove: (row: any) => void;
};

export const ClubProgrammeManageClubsTable: React.FC<Props> = ({
  rows,
  columns,
  selectedProgrammeClubs,
  canEdit,
  isBulkDisabling,
  savingIds,
  onToggleRowSelection,
  onToggleEnabled,
  onRowClick,
  onOpenBookingForm,
  onOpenInvites,
  onRequestRemove
}) => {
  const [actionMenu, setActionMenu] = React.useState<{ rowId: string; top: number; left: number } | null>(null);

  React.useEffect(() => {
    if (!actionMenu) return undefined;
    const close = () => setActionMenu(null);
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', closeOnEscape);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', closeOnEscape);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [actionMenu]);

  const selectedIds = new Set(
    (selectedProgrammeClubs || [])
      .map(row => String(row?.programmeClubId || row?.pc?._id || row?.pc?.id || ''))
      .filter(Boolean)
  );

  const actionRow = actionMenu
    ? rows.find(row => String(row?.programmeClubId || '') === String(actionMenu.rowId))
    : null;

  const openActionMenu = (event: React.MouseEvent<HTMLButtonElement>, row: any) => {
    event.stopPropagation();
    const rowId = String(row?.programmeClubId || '');
    if (!rowId) return;
    if (actionMenu?.rowId === rowId) {
      setActionMenu(null);
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const menuWidth = 228;
    const estimatedHeight = 190;
    const top =
      rect.bottom + 6 + estimatedHeight <= window.innerHeight
        ? rect.bottom + 6
        : Math.max(12, rect.top - estimatedHeight - 6);
    const left = Math.max(12, Math.min(window.innerWidth - menuWidth - 12, rect.right - menuWidth));
    setActionMenu({ rowId, top, left });
  };

  const runAction = (action: () => void) => {
    setActionMenu(null);
    action();
  };

  const renderCell = (row: any, column: ManageClubColumn) => {
    switch (column.field) {
      case 'club':
        return (
          <div className="pcClubCell">
            <span className="pcClubIcon">
              <IconWhistle />
            </span>
            <span className="pcClubText">
              <span className="pcClubName" title={row.isClubDetailsLoaded ? row.name : 'Loading club details...'}>
                {row.isClubDetailsLoaded ? row.name : 'Loading...'}
              </span>
              <span className="pcClubSport">{row.sportName || '—'}</span>
            </span>
          </div>
        );
      case 'dateTime':
        return (
          <div className="pcWhenCell">
            <span>{row.day || '—'}</span>
            <small>{row.time || '—'}</small>
          </div>
        );
      case 'audience':
        return (
          <>
            {row.gender || '—'} · {row.ages || '—'}
          </>
        );
      case 'places':
        return <span className="pcNumberCell">{row.placesLabel || 'Unlimited'}</span>;
      case 'bookingForm':
        return <span className={row.fieldsBadgeClass}>{row.fieldsDisplayLabel}</span>;
      case 'invites':
        return row.invitesOnly ? (
          <span className="pcInvitesCell">
            <span className={row.invitesBadgeClass}>{row.invitesDisplayLabel}</span>
            <span className="pcTableMutedText">{row.invitedCount || 0} invited</span>
          </span>
        ) : (
          <span className={row.invitesBadgeClass}>{row.invitesDisplayLabel}</span>
        );
      case 'status': {
        const programmeClubId = row.programmeClubId;
        const saving = !!savingIds[programmeClubId];
        const enabled = !!(row.pc && row.pc.isEnabled);
        return (
          <span className={`pcStatusDisplay ${saving || isBulkDisabling ? 'isSaving' : ''}`}>
            <span
              className={enabled ? 'pcBadge pcBadge--success pcBadge--dot' : 'pcBadge pcBadge--neutral pcBadge--dot'}
            >
              {saving ? 'Updating…' : enabled ? 'Enabled' : 'Disabled'}
            </span>
          </span>
        );
      }
      default:
        return null;
    }
  };

  return (
    <>
      <ClubProgrammeDataTableShell maxHeight="none">
        <table className="pcDataTable">
          <thead>
            <tr>
              <th className="pcTableSelectCol">
                <span className="sr-only">Select</span>
              </th>
              {columns.map(column => (
                <th key={column.field} style={{ width: column.width }}>
                  {column.text}
                </th>
              ))}
              <th style={{ width: 52 }} />
            </tr>
          </thead>

          <tbody>
            {rows.length ? (
              rows.map(row => {
                const programmeClubId = row.programmeClubId;
                const selected = selectedIds.has(String(programmeClubId));

                return (
                  <tr
                    key={programmeClubId}
                    className={selected ? 'pcDataTableRow pcDataTableRow--selected' : 'pcDataTableRow'}
                    onClick={() => onRowClick(row)}
                  >
                    <td className="pcTableSelectCol" onClick={e => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="pcCheckbox"
                        checked={selected}
                        disabled={isBulkDisabling}
                        onChange={() => onToggleRowSelection(row)}
                      />
                    </td>
                    {columns.map(column => (
                      <td key={column.field}>{renderCell(row, column)}</td>
                    ))}
                    <td onClick={e => e.stopPropagation()}>
                      <button
                        type="button"
                        className={`pcIconBtn pcRowActionsButton ${
                          actionMenu?.rowId === String(programmeClubId) ? 'isActive' : ''
                        }`}
                        aria-label="Club actions"
                        aria-haspopup="menu"
                        aria-expanded={actionMenu?.rowId === String(programmeClubId)}
                        onClick={event => openActionMenu(event, row)}
                      >
                        <IconMore />
                      </button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={columns.length + 2} className="pcDataTableEmpty">
                  No clubs match these filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </ClubProgrammeDataTableShell>

      {actionMenu && actionRow && typeof document !== 'undefined'
        ? createPortal(
            <div
              className="pcRowActionMenu"
              role="menu"
              style={{ top: actionMenu.top, left: actionMenu.left }}
              onMouseDown={event => event.stopPropagation()}
              onClick={event => event.stopPropagation()}
            >
              <button
                type="button"
                role="menuitem"
                disabled={!canEdit}
                onClick={() => runAction(() => onOpenBookingForm(actionRow))}
              >
                <RowActionIcon icon="form" />
                <span>Edit booking form</span>
              </button>
              <button
                type="button"
                role="menuitem"
                disabled={!canEdit}
                onClick={() => runAction(() => onOpenInvites(actionRow))}
              >
                <RowActionIcon icon="mail" />
                <span>Edit invite list</span>
              </button>
              <button
                type="button"
                role="menuitem"
                disabled={!canEdit || !!savingIds[String(actionRow.programmeClubId)] || isBulkDisabling}
                onClick={() =>
                  runAction(() => onToggleEnabled(String(actionRow.programmeClubId), !actionRow.isDisabled))
                }
              >
                <RowActionIcon icon="lock" />
                <span>{actionRow.isDisabled ? 'Enable club' : 'Disable club'}</span>
              </button>
              <div className="pcRowActionMenuDivider" />
              <button
                type="button"
                role="menuitem"
                className="isDanger"
                disabled={!canEdit || !actionRow.isDisabled || isBulkDisabling}
                title={actionRow.isDisabled ? undefined : 'Disable the club before removing it'}
                onClick={() => runAction(() => onRequestRemove(actionRow))}
              >
                <RowActionIcon icon="trash" />
                <span>Remove from programme</span>
              </button>
            </div>,
            document.body
          )
        : null}
    </>
  );
};
